import React from 'react';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import { scaleByPeriod, type PeriodValue } from '../../lib/period';
import { tcCampaignById } from '../../treatment/mockData';

export interface TiCampaignReportViewProps {
  campaignId: string;
  /** The chosen period (spec 41). */
  period: PeriodValue;
  onPeriodChange: (value: PeriodValue) => void;
  onBack?: () => void;
  onEdit?: () => void;
}

/** Freshly-diagnosed's exact per-range figures (§4.2). */
const FRESH: Record<'7d' | '30d' | '60d' | 'all', { messaged: number; opened: number; booked: number; value: string }> = {
  '7d': { messaged: 24, opened: 17, booked: 5, value: '$9,100' },
  '30d': { messaged: 96, opened: 68, booked: 21, value: '$38,200' },
  '60d': { messaged: 181, opened: 127, booked: 39, value: '$70,400' },
  all: { messaged: 240, opened: 171, booked: 52, value: '$94,600' },
};

const STARTED_ISO = '2026-08-01';

function keyFor(value: PeriodValue): '7d' | '30d' | '60d' | 'all' {
  const k = value.key;
  return k === '7d' || k === '30d' || k === '60d' || k === 'all' ? k : '30d';
}

/**
 * A campaign's report (staging fix 29 §3.2), now period-aware (spec 41 §4.2):
 * the selector drives every number, the rows add up to the tiles (Email books 10
 * at 30 days so the steps total 21), and each tile shows its rate — hidden under
 * 7 days. Freshly-diagnosed carries the spec's exact table; others scale.
 */
export function TiCampaignReportView({ campaignId, period, onPeriodChange, onBack, onEdit }: TiCampaignReportViewProps) {
  const c = tcCampaignById(campaignId);

  if (c === undefined) {
    return (
      <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
        <p className="text-[13px] text-[var(--text-muted)]">That campaign no longer exists.</p>
        <Button variant="secondary" size="sm" onClick={onBack}>Back to campaigns</Button>
      </div>
    );
  }

  const isFresh = campaignId === 'freshly-diagnosed';
  const custom = period.key === 'custom';
  let messaged: number;
  let opened: number;
  let booked: number;
  let value: string;
  if (isFresh) {
    const col = FRESH[keyFor(period)];
    messaged = custom ? scaleByPeriod(FRESH['30d'].messaged, period, STARTED_ISO) : col.messaged;
    opened = custom ? scaleByPeriod(FRESH['30d'].opened, period, STARTED_ISO) : col.opened;
    booked = custom ? scaleByPeriod(FRESH['30d'].booked, period, STARTED_ISO) : col.booked;
    value = custom ? `$${scaleByPeriod(38200, period, STARTED_ISO).toLocaleString('en-US')}` : col.value;
  } else {
    messaged = scaleByPeriod(c.messaged ?? 0, period, STARTED_ISO);
    opened = scaleByPeriod(c.openedPlan ?? 0, period, STARTED_ISO);
    booked = scaleByPeriod(c.booked30 ?? 0, period, STARTED_ISO);
    value = `$${scaleByPeriod(Number((c.value30 ?? '$0').replace(/[^0-9]/g, '')), period, STARTED_ISO).toLocaleString('en-US')}`;
  }

  // Rates show from 7 days up; the report variant never offers 24h (§3.2).
  const ratesOn = period.key === 'custom' ? ratesCustom(period) : period.key !== '24h';
  const pct = (n: number) => (messaged === 0 ? '—' : `${String(Math.round((n / messaged) * 100))}% of messaged`);

  // Two steps whose Booked add up to the tile (rule 6): Email books ~24/52, Text the rest.
  const emailBooked = Math.round(booked * (24 / 52));
  const textBooked = Math.max(0, booked - emailBooked);
  const steps = [
    { n: 1, channel: 'Text', sent: messaged, opened: Math.round(opened * 0.6), booked: textBooked },
    { n: 2, channel: 'Email', sent: Math.round(messaged * 0.55), opened: Math.round(opened * 0.4), booked: emailBooked },
  ];

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        Treatment Intelligence › Campaigns › {c.name}
      </div>
      <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 text-[24px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">{c.name}</h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{c.description} · running since 1 Aug</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector variant="report" value={period} onChange={onPeriodChange} startedISO={STARTED_ISO} startedLabel="1 Aug 2026" />
          <Button variant="secondary" size="sm" onClick={onBack}>Back</Button>
          <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Messaged" value={messaged.toLocaleString('en-US')} sub="patients" />
        <Stat label="Opened their plan" value={opened.toLocaleString('en-US')} sub={ratesOn ? pct(opened) : '—'} subMuted={!ratesOn} />
        <Stat label="Booked" value={booked.toLocaleString('en-US')} sub={ratesOn ? pct(booked) : '—'} subMuted={!ratesOn} />
        <Stat label="Treatment booked" value={value} accent />
      </div>

      <div className="mt-5 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className="grid grid-cols-[0.5fr_1fr_0.8fr_0.9fr_0.7fr] gap-x-3 border-b border-[var(--border-default)] bg-[var(--surface-header)] px-4 py-2.5 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
          <span>Step</span><span>Channel</span><span className="text-right">Sent</span><span className="text-right">Opened plan</span><span className="text-right">Booked</span>
        </div>
        {steps.map((s) => (
          <div key={s.n} className="grid grid-cols-[0.5fr_1fr_0.8fr_0.9fr_0.7fr] items-center gap-x-3 border-t border-[var(--border-default)] px-4 py-2.5 text-[12.5px] first:border-0">
            <span className="font-[var(--font-mono)] text-[var(--text-muted)]">{s.n}</span>
            <span className="inline-flex items-center gap-1.5 text-[var(--text-secondary)]">
              <Icon kind={s.channel === 'Email' ? 'mail' : 'chat'} size={12} stroke={2} className="text-[var(--text-muted)]" />{s.channel}
            </span>
            <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{s.sent}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{s.opened}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{s.booked}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Custom ranges show rates only from 7 days up (§3.2). */
function ratesCustom(value: PeriodValue): boolean {
  if (!value.from || !value.to) return true;
  const from = new Date(value.from).getTime();
  const to = new Date(value.to).getTime();
  return (to - from) / 86_400_000 >= 7;
}

function Stat({ label, value, sub, subMuted, accent }: { label: string; value: string; sub?: string; subMuted?: boolean; accent?: boolean }) {
  return (
    <div className="rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5 shadow-[var(--shadow-card)]">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
      <div className={`mt-1 font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.03em] ${accent === true ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]'}`}>{value}</div>
      {sub !== undefined ? (
        <div className="mt-0.5 text-[12px] text-[var(--text-muted)]">{subMuted === true ? 'Shown for 7 days or more' : sub}</div>
      ) : null}
    </div>
  );
}
